import React from "react";

import {
  COUNT_IN_BEATS,
  COUNT_IN_BEAT_MS,
  COUNT_IN_EXIT_MS,
  COUNT_IN_GO_MS,
} from "../constants";
import { playCountInBeat, playCountInGo } from "../sound";

// Speedrun start gate + 3-2-1-GO count-in.
//
// markStarted (round timer) doesn't fire on mode entry — it fires when
// the egg has actually arrived at the round's first marc. The trigger
// condition is "egg's reference is loaded for the round's
// currentMarcId AND no tween is in progress" — that's the canonical
// "settled at the right pose" signal, regardless of whether a tween
// fired (e.g., if landing's last marc happens to coincide with
// speedrun's first, no tween needs to fire and the egg is already in
// position).
//
// `tween.tweeningRef.current` is read synchronously instead of
// `tween.tweening` because this effect runs in the same commit cycle
// that the tween's `setTweening(true)` schedules — by the time we read
// `tween.tweening` it's still the prior render's value (false), so we'd
// race ahead of the slide and start the timer ~16ms before it actually
// starts. The ref is mutated synchronously inside the tween hook's
// effect, which is queued before this one as long as the tween hook is
// called earlier in the parent. `tween.tweening` IS in the dep list so
// we re-check on the tween-end transition.
//
// `firedRef` gates the call so subsequent tween-ends (between marcs in
// the same round) don't re-trigger; reset on inactive so the next
// entry into speedrun re-arms.
//
// Once the gate opens, we run a 3-2-1-GO count-in over the score
// panel and fire markStarted at the end of the GO beat (so the round
// timer starts the moment the player can actually draw). `?debug`
// bypasses the count-in entirely so iteration stays fast.
export default function useSpeedrunCountIn({
  active,
  currentMarcId,
  eggReferenceLoadedForMarcId,
  tween,
  markStarted,
  debug,
}) {
  const tweeningRef = tween.tweeningRef;
  const tweening = tween.tweening;
  const firedRef = React.useRef(false);
  const [countIn, setCountIn] = React.useState({
    numeral: null,
    exiting: false,
  });
  const timersRef = React.useRef([]);
  const clearTimers = React.useCallback(() => {
    for (const t of timersRef.current) clearTimeout(t);
    timersRef.current = [];
  }, []);

  React.useEffect(() => {
    if (!active) {
      firedRef.current = false;
      clearTimers();
      setCountIn({ numeral: null, exiting: false });
      return;
    }
    if (firedRef.current) return;
    if (currentMarcId == null) return;
    if (eggReferenceLoadedForMarcId !== currentMarcId) return;
    if (tweeningRef.current) return;
    firedRef.current = true;

    if (debug) {
      markStarted();
      return;
    }

    // 3 / 2 / 1 each hold for COUNT_IN_BEAT_MS; GO holds for the
    // shorter COUNT_IN_GO_MS (linger less, get into the round). The
    // first numeral renders synchronously on the first commit after
    // the effect fires; subsequent beats are queued via setTimeout.
    setCountIn({ numeral: COUNT_IN_BEATS[0], exiting: false });
    playCountInBeat();
    let elapsed = 0;
    for (let i = 1; i < COUNT_IN_BEATS.length; i++) {
      elapsed += COUNT_IN_BEAT_MS;
      const isGo = i === COUNT_IN_BEATS.length - 1;
      const t = setTimeout(() => {
        setCountIn({ numeral: COUNT_IN_BEATS[i], exiting: false });
        if (isGo) playCountInGo();
        else playCountInBeat();
      }, elapsed);
      timersRef.current.push(t);
    }
    // GO beat ends → fire markStarted (timer starts, input unlocks),
    // start the exit fade, then clear count-in state once the fade
    // settles.
    const goEnd = elapsed + COUNT_IN_GO_MS;
    const tStart = setTimeout(() => {
      markStarted();
      setCountIn((c) => ({ numeral: c.numeral, exiting: true }));
    }, goEnd);
    timersRef.current.push(tStart);
    const tClear = setTimeout(() => {
      setCountIn({ numeral: null, exiting: false });
    }, goEnd + COUNT_IN_EXIT_MS);
    timersRef.current.push(tClear);
  }, [
    active,
    currentMarcId,
    eggReferenceLoadedForMarcId,
    tweening,
    markStarted,
    tweeningRef,
    debug,
    clearTimers,
  ]);

  return countIn;
}
